import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchSizSections,
  fetchSizCatalog,
  fetchSizNorms,
  fetchSizWidget,
  fetchSizEmployees,
  fetchSizEmployeeProfile,
  fetchSizEmployeeRequiredItems,
  fetchSizEmployeeIssuances,
  fetchSizEmployeeHistory,
  type CatalogParams,
  type NormsParams,
  type SizEmployeesParams,
} from './sizApi';

/** GET /v1/ppe/sections — состав и порядок вкладок только из ответа API. */
export function useSizSections() {
  return useQuery({
    queryKey: ['siz', 'sections'],
    queryFn: fetchSizSections,
    staleTime: 60 * 1000,
  });
}

// Catalog
export function useSizCatalog(params: CatalogParams = {}) {
  const baseParams = { ...params };
  delete baseParams.page_token;
  return useInfiniteQuery({
    queryKey: ['siz', 'catalog', baseParams],
    queryFn: ({ pageParam }) => fetchSizCatalog({ ...baseParams, page_token: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });
}

// Norms
export function useSizNorms(params: NormsParams = {}) {
  const baseParams = { ...params };
  delete baseParams.page_token;
  return useInfiniteQuery({
    queryKey: ['siz', 'norms', baseParams],
    queryFn: ({ pageParam }) => fetchSizNorms({ ...baseParams, page_token: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });
}

// Widget
export function useSizWidget() {
  return useQuery({
    queryKey: ['siz', 'widget'],
    queryFn: fetchSizWidget,
    staleTime: 30 * 1000,
  });
}

// Employees
export function useSizEmployees(params: SizEmployeesParams = {}) {
  const baseParams = { ...params };
  delete baseParams.page_token;
  return useInfiniteQuery({
    queryKey: ['siz', 'employees', baseParams],
    queryFn: ({ pageParam }) => fetchSizEmployees({ ...baseParams, page_token: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });
}

// Employee profile
export function useSizEmployeeProfile(employeeId: string | null) {
  return useQuery({
    queryKey: ['siz', 'employee', employeeId, 'profile'],
    queryFn: () => fetchSizEmployeeProfile(employeeId!),
    enabled: !!employeeId,
    staleTime: 60 * 1000,
  });
}

// Employee required items
export function useSizEmployeeRequiredItems(employeeId: string | null) {
  return useQuery({
    queryKey: ['siz', 'employee', employeeId, 'required-items'],
    queryFn: () => fetchSizEmployeeRequiredItems(employeeId!),
    enabled: !!employeeId,
    staleTime: 60 * 1000,
  });
}

// Employee issuances
export function useSizEmployeeIssuances(employeeId: string | null) {
  return useQuery({
    queryKey: ['siz', 'employee', employeeId, 'issuances'],
    queryFn: () => fetchSizEmployeeIssuances(employeeId!),
    enabled: !!employeeId,
    staleTime: 60 * 1000,
  });
}

// Employee history
export function useSizEmployeeHistory(employeeId: string | null) {
  return useQuery({
    queryKey: ['siz', 'employee', employeeId, 'history'],
    queryFn: () => fetchSizEmployeeHistory(employeeId!),
    enabled: !!employeeId,
    staleTime: 60 * 1000,
  });
}
